/**
 * Фабрика операций и построитель батчей
 */

import { generateId } from '../utils/helpers';
import { ICSElement } from '../types/interfaces';
import {
  BatchOperation,
  BatchOperationFactory,
  BatchBuilder,
  BatchConfig,
  BatchResult,
  BatchOperationType,
  BatchPriority,
  BatchExecutionContext,
  IBatchManager
} from '../types/batch-interfaces';

/**
 * Реализация фабрики операций
 */
export class DefaultBatchOperationFactory implements BatchOperationFactory {
  
  /**
   * Создать операцию создания элемента
   */
  createElementOperation(elementData: any): BatchOperation {
    return {
      id: generateId(),
      type: BatchOperationType.CREATE,
      priority: BatchPriority.NORMAL,
      params: { elementData },
      execute: async (context: BatchExecutionContext) => {
        // Здесь должна быть логика создания элемента
        // В реальной реализации это будет вызов CSElement API
        const element = {
          id: generateId(),
          ...elementData,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        context.sharedData.set(`created_${element.id}`, element);
        return element;
      },
      validate: async (context: BatchExecutionContext) => {
        const errors: string[] = [];
        const warnings: string[] = [];
        
        if (!context.operation.params?.elementData) {
          errors.push('Отсутствуют данные для создания элемента');
        }
        
        if (context.operation.params?.elementData && !context.operation.params.elementData.type) {
          warnings.push('Не указан тип элемента, будет использован тип по умолчанию');
        }
        
        return {
          valid: errors.length === 0,
          errors,
          warnings
        };
      },
      metadata: {
        operationType: 'createElement',
        description: 'Создание нового элемента'
      }
    };
  }

  /**
   * Создать операцию обновления элемента
   */
  updateElementOperation(element: ICSElement, updates: any): BatchOperation {
    return {
      id: generateId(),
      type: BatchOperationType.UPDATE,
      priority: BatchPriority.NORMAL,
      target: element,
      params: { updates },
      execute: async (context: BatchExecutionContext) => {
        const target = context.operation.target;
        if (!target) {
          throw new Error('Целевой элемент не найден');
        }
        
        // Применяем обновления
        const updatedElement = {
          ...target,
          ...context.operation.params?.updates,
          updatedAt: new Date()
        };
        
        context.sharedData.set(`updated_${target.id}`, updatedElement);
        return updatedElement;
      },
      validate: async (context: BatchExecutionContext) => {
        const errors: string[] = [];
        const warnings: string[] = [];
        
        if (!context.operation.target) {
          errors.push('Не указан целевой элемент для обновления');
        }
        
        if (!context.operation.params?.updates) {
          errors.push('Отсутствуют данные для обновления');
        }
        
        return {
          valid: errors.length === 0,
          errors,
          warnings
        };
      },
      metadata: {
        operationType: 'updateElement',
        description: `Обновление элемента ${element.id}`,
        elementId: element.id
      }
    };
  }

  /**
   * Создать операцию удаления элемента
   */
  deleteElementOperation(element: ICSElement): BatchOperation {
    return {
      id: generateId(),
      type: BatchOperationType.DELETE,
      priority: BatchPriority.HIGH,
      target: element,
      execute: async (context: BatchExecutionContext) => {
        const target = context.operation.target;
        if (!target) {
          throw new Error('Целевой элемент не найден');
        }
        
        // Помечаем элемент как удаленный
        const deletedElement = {
          ...target,
          deleted: true,
          deletedAt: new Date()
        };
        
        context.sharedData.set(`deleted_${target.id}`, deletedElement);
        return { deleted: true, elementId: target.id };
      },
      rollback: async (context: BatchExecutionContext) => {
        const target = context.operation.target;
        if (target) {
          // Восстанавливаем элемент
          context.sharedData.set(`restored_${target.id}`, target);
        }
      },
      validate: async (context: BatchExecutionContext) => {
        const errors: string[] = [];
        const warnings: string[] = [];
        
        if (!context.operation.target) {
          errors.push('Не указан элемент для удаления');
        }
        
        return {
          valid: errors.length === 0,
          errors,
          warnings
        };
      },
      metadata: {
        operationType: 'deleteElement',
        description: `Удаление элемента ${element.id}`,
        elementId: element.id
      }
    };
  }

  /**
   * Создать операцию перемещения элемента
   */
  moveElementOperation(element: ICSElement, newParent: ICSElement): BatchOperation {
    return {
      id: generateId(),
      type: BatchOperationType.MOVE,
      priority: BatchPriority.NORMAL,
      target: element,
      params: { newParent },
      execute: async (context: BatchExecutionContext) => {
        const target = context.operation.target;
        const newParent = context.operation.params?.newParent;
        
        if (!target || !newParent) {
          throw new Error('Не указан элемент или новый родитель');
        }
        
        const movedElement = {
          ...target,
          parentId: newParent.id,
          updatedAt: new Date()
        };
        
        context.sharedData.set(`moved_${target.id}`, movedElement);
        return movedElement;
      },
      validate: async (context: BatchExecutionContext) => {
        const errors: string[] = [];
        const warnings: string[] = [];
        
        if (!context.operation.target) {
          errors.push('Не указан элемент для перемещения');
        }
        
        if (!context.operation.params?.newParent) {
          errors.push('Не указан новый родительский элемент');
        }
        
        return {
          valid: errors.length === 0,
          errors,
          warnings
        };
      },
      metadata: {
        operationType: 'moveElement',
        description: `Перемещение элемента ${element.id} к ${newParent.id}`,
        elementId: element.id,
        newParentId: newParent.id
      }
    };
  }

  /**
   * Создать операцию копирования элемента
   */
  copyElementOperation(element: ICSElement, target: ICSElement): BatchOperation {
    return {
      id: generateId(),
      type: BatchOperationType.COPY,
      priority: BatchPriority.NORMAL,
      target: element,
      params: { target },
      execute: async (context: BatchExecutionContext) => {
        const source = context.operation.target;
        const target = context.operation.params?.target;
        
        if (!source || !target) {
          throw new Error('Не указан исходный элемент или цель копирования');
        }
        
        const copiedElement = {
          ...source,
          id: generateId(),
          parentId: target.id,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        context.sharedData.set(`copied_${copiedElement.id}`, copiedElement);
        return copiedElement;
      },
      validate: async (context: BatchExecutionContext) => {
        const errors: string[] = [];
        const warnings: string[] = [];
        
        if (!context.operation.target) {
          errors.push('Не указан элемент для копирования');
        }
        
        if (!context.operation.params?.target) {
          errors.push('Не указана цель копирования');
        }
        
        return {
          valid: errors.length === 0,
          errors,
          warnings
        };
      },
      metadata: {
        operationType: 'copyElement',
        description: `Копирование элемента ${element.id} в ${target.id}`,
        sourceElementId: element.id,
        targetElementId: target.id
      }
    };
  }

  /**
   * Создать операцию валидации
   */
  validateElementOperation(element: ICSElement): BatchOperation {
    return {
      id: generateId(),
      type: BatchOperationType.VALIDATE,
      priority: BatchPriority.LOW,
      target: element,
      execute: async (context: BatchExecutionContext) => {
        const target = context.operation.target;
        if (!target) {
          throw new Error('Целевой элемент не найден');
        }
        
        // Выполняем валидацию элемента
        const validationResult = {
          elementId: target.id,
          valid: true,
          errors: [] as string[],
          warnings: [] as string[]
        };
        
        // Базовая валидация
        if (!target.id) {
          validationResult.valid = false;
          validationResult.errors.push('Отсутствует ID элемента');
        }
        
        if (!target.name) {
          validationResult.warnings.push('Не указано имя элемента');
        }
        
        context.sharedData.set(`validation_${target.id}`, validationResult);
        return validationResult;
      },
      metadata: {
        operationType: 'validateElement',
        description: `Валидация элемента ${element.id}`,
        elementId: element.id
      }
    };
  }

  /**
   * Создать пользовательскую операцию
   */
  customOperation(
    id: string, 
    executor: (context: BatchExecutionContext) => Promise<any>
  ): BatchOperation {
    return {
      id,
      type: BatchOperationType.CUSTOM,
      priority: BatchPriority.NORMAL,
      execute: executor,
      metadata: {
        operationType: 'custom',
        description: `Пользовательская операция ${id}`
      }
    };
  }
}

/**
 * Построитель батчей
 */
export class DefaultBatchBuilder implements BatchBuilder {
  private batchManager: IBatchManager;
  private operationFactory: BatchOperationFactory;
  private batchId: string;
  private config: Partial<BatchConfig> = {};

  constructor(batchManager: IBatchManager, operationFactory?: BatchOperationFactory) {
    this.batchManager = batchManager;
    this.operationFactory = operationFactory || new DefaultBatchOperationFactory();
    this.batchId = this.batchManager.createBatch();
  }

  /**
   * Установить конфигурацию
   */
  withConfig(config: Partial<BatchConfig>): BatchBuilder {
    this.config = { ...this.config, ...config };
    // Создаем новый батч с обновленной конфигурацией
    this.batchManager.removeBatch(this.batchId);
    this.batchId = this.batchManager.createBatch(this.config);
    return this;
  }

  /**
   * Добавить операцию
   */
  addOperation(operation: BatchOperation): BatchBuilder {
    this.batchManager.addOperation(this.batchId, operation);
    return this;
  }

  /**
   * Добавить операции
   */
  addOperations(operations: BatchOperation[]): BatchBuilder {
    this.batchManager.addOperations(this.batchId, operations);
    return this;
  }

  /**
   * Добавить операцию создания
   */
  createElement(elementData: any): BatchBuilder {
    const operation = this.operationFactory.createElementOperation(elementData);
    return this.addOperation(operation);
  }

  /**
   * Добавить операцию обновления
   */
  updateElement(element: ICSElement, updates: any): BatchBuilder {
    const operation = this.operationFactory.updateElementOperation(element, updates);
    return this.addOperation(operation);
  }

  /**
   * Добавить операцию удаления
   */
  deleteElement(element: ICSElement): BatchBuilder {
    const operation = this.operationFactory.deleteElementOperation(element);
    return this.addOperation(operation);
  }

  /**
   * Добавить условную операцию
   */
  when(
    condition: (context: BatchExecutionContext) => boolean, 
    operation: BatchOperation
  ): BatchBuilder {
    // Оборачиваем операцию в условную логику
    const conditionalOperation: BatchOperation = {
      ...operation,
      id: `conditional_${operation.id}`,
      execute: async (context: BatchExecutionContext) => {
        if (condition(context)) {
          return operation.execute(context);
        } else {
          return { skipped: true, reason: 'Условие не выполнено' };
        }
      },
      metadata: {
        ...operation.metadata,
        conditional: true,
        originalOperationId: operation.id
      }
    };

    return this.addOperation(conditionalOperation);
  }

  /**
   * Построить и выполнить батч
   */
  async execute(): Promise<BatchResult> {
    return this.batchManager.executeBatch(this.batchId);
  }

  /**
   * Построить батч без выполнения
   */
  build(): string {
    return this.batchId;
  }
}

/**
 * Утилитарные функции для создания операций
 */
export const BatchOperations = {
  /**
   * Создать операцию задержки
   */
  delay(ms: number): BatchOperation {
    return {
      id: generateId(),
      type: BatchOperationType.CUSTOM,
      priority: BatchPriority.LOW,
      params: { delay: ms },
      execute: async () => {
        await new Promise(resolve => setTimeout(resolve, ms));
        return { delayed: ms };
      },
      metadata: {
        operationType: 'delay',
        description: `Задержка на ${ms}мс`
      }
    };
  },

  /**
   * Создать операцию логирования
   */
  log(message: string, level: 'info' | 'warn' | 'error' = 'info'): BatchOperation {
    return {
      id: generateId(),
      type: BatchOperationType.CUSTOM,
      priority: BatchPriority.LOW,
      params: { message, level },
      execute: async (context) => {
        const logEntry = {
          timestamp: new Date(),
          level,
          message,
          batchId: context.batchId,
          operationId: context.operation.id
        };
        
        console[level](message);
        context.sharedData.set(`log_${context.operation.id}`, logEntry);
        
        return logEntry;
      },
      metadata: {
        operationType: 'log',
        description: `Логирование: ${message}`
      }
    };
  },

  /**
   * Создать операцию проверки условия
   */
  assert(
    condition: (context: BatchExecutionContext) => boolean, 
    message: string
  ): BatchOperation {
    return {
      id: generateId(),
      type: BatchOperationType.VALIDATE,
      priority: BatchPriority.HIGH,
      params: { condition, message },
      execute: async (context) => {
        const result = context.operation.params?.condition(context);
        if (!result) {
          throw new Error(context.operation.params?.message || 'Условие не выполнено');
        }
        return { assertion: true, message: context.operation.params?.message };
      },
      metadata: {
        operationType: 'assert',
        description: `Проверка условия: ${message}`
      }
    };
  }
}; 